import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import AccessDenied from "components/AccessDenied";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { useEffect, useMemo, useReducer, useState } from "react";
import auditTemplatesService from "services/audit-templates.service";
import { RowTemplate2 } from "./components/RowTemplate";
import TemplateDialog from "./components/TemplateDialog";
import AlertDialog from "components/AlertDialog";
import TableSearch from "components/TableSearch";
import { useLanguage } from "contexts/language.context";
import DataTable from "examples/Tables/DataTable";
import { IconButton, Pagination } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useSearchParams } from "react-router-dom";

export default function AllTemplates({ permissions }) {
  const [originalTemplates, setOriginalTemplates] = useState();
  const [loading, setLoading] = useState(false);
  const { label } = useLanguage();
  const [templates, setTemplates] = useState();
  const [open, changeOpen] = useReducer((state, action) => {
    return { ...state, [action.field]: action.value };
  }, {});
  const [query, setQuery] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [count, setCount] = useState(0);

  async function handleFetchAuditTemplates(internalQuery) {
    setLoading(true);
    const [response, data] = await auditTemplatesService.getAuditTemplates(
      internalQuery || query,
    );

    setCount(data.count);
    if (response.ok) {
      setOriginalTemplates(data.data);
    }
    setLoading(false);
    return data.data;
  }

  async function handleCreateAuditTemplate(newAuditTemplate, requested) {
    const [response, data] = await auditTemplatesService.createAuditTemplate(
      newAuditTemplate,
      requested ? { request: true } : {},
    );
    if (response.ok) {
      await handleFetchAuditTemplates();
      // setOriginalTemplates(data);
      return { success: true };
    } else {
      return { error: "Internal error occurred" };
    }
  }

  async function handleUpdateAuditTemplate(id, newAuditTemplate) {
    const [response, data] = await auditTemplatesService.updateAuditTemplate(
      id,
      newAuditTemplate,
    );
    if (response.ok) {
      await handleFetchAuditTemplates();
      // setOriginalTemplates(data);
      return { success: true };
    } else {
      return { error: "Internal error occurred" };
    }
  }

  async function handleRemoveAuditTemplate(id) {
    const [response, data] =
      await auditTemplatesService.removeAuditTemplate(id);
    if (response.ok) {
      await handleFetchAuditTemplates();
      // setOriginalTemplates(data);
      return { success: true };
    } else {
      return { error: "Internal error occurred" };
    }
  }

  function arrangeAuditTemplates(data) {
    return data.map((element) => {
      const template = {
        id: element.id,
        name: element.name,
        subcategoryId: element.subcategoryId,
        subcategory: element.subcategory,
        requestedAt: element.requestedAt,
        approvedAt: element.approvedAt,
        requestedFromId: element.requestedFromId,
        account: element.account,
      };
      const pillars = {};
      element.templateKpis.forEach((templateKpi) => {
        if (!templateKpi?.kpi?.pillar?.id) return;

        if (!pillars[templateKpi?.kpi?.pillar?.id]) {
          pillars[templateKpi?.kpi?.pillar?.id] = {
            ...templateKpi?.kpi?.pillar,
            kpis: [],
            weight: 0,
          };
        }
        pillars[templateKpi?.kpi?.pillar?.id].kpis.push({
          ...templateKpi.kpi,
          description: templateKpi.description ?? templateKpi?.kpi?.description,
          weight: templateKpi.weight ?? templateKpi.kpi.weight,
        });
        pillars[templateKpi?.kpi?.pillar?.id].weight +=
          templateKpi.weight ?? templateKpi.kpi.weight;
      });

      template.pillars = Object.values(pillars);
      return template;
    });
  }

  const onClickTemplate = useMemo(() => {
    return {
      update: (template) => {
        const initial = originalTemplates.find((t) => t.id == template.id);
        initial.templateKpis.forEach((kpi, index) => {
          initial.templateKpis[index].kpiId =
            initial.templateKpis[index].kpi.id;
        });
        changeOpen({
          field: "templateDialog",
          value: { mode: "update", initial },
        });
      },
      remove: (id) => {
        changeOpen({ field: "alertRemoveDialog", value: { id } });
      },
      approve: (template) => {
        handleUpdateAuditTemplate(template.id, { approvedAt: new Date() });
      },
      duplicate: (template) => {
        const initial = originalTemplates.find((t) => t.id == template.id);
        initial.templateKpis.forEach((kpi, index) => {
          initial.templateKpis[index].kpiId =
            initial.templateKpis[index].kpi.id;
        });
        initial.name += " Copy";
        changeOpen({
          field: "templateDialog",
          value: { mode: "create", initial },
        });
      },
    };
  }, [changeOpen, originalTemplates]);

  useEffect(() => {
    if (originalTemplates)
      setTemplates(arrangeAuditTemplates(originalTemplates));
  }, [originalTemplates]);

  const columns = [
    {
      Header: label("name-singular"),
      accessor: "name",
      width: "70%",

      bodyCellStyle: {
        whiteSpace: "unset",
        overflow: "unset",
        textOverflow: "unset",
      },
    },
    {
      Header: label("subcategory-singular"),
      accessor: "subcategory",
      width: "15%",
    },

    {
      Header: label("account-singular"),
      accessor: "account",
      width: "15%",
    },
  ];

  if (permissions.update || permissions.delete) {
    columns.push({
      Header: "",
      accessor: "actions",
      align: "right",
    });
  }

  const rows = useMemo(() => {
    return (
      templates?.map((template) => {
        return {
          id: template.id,
          data: template,
          name: template.name,
          subcategory: template?.subcategory?.description || "None",
          account: template?.account?.name || "None",
          actions: (
            <MDBox
              display="flex"
              flexDirection="row"
              justifyContent="end"
              alignItems="center"
            >
              {permissions.update && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickTemplate.update(template);
                  }}
                >
                  <Icon>edit</Icon>
                </IconButton>
              )}
              {permissions.create && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickTemplate.duplicate(template);
                  }}
                >
                  <Icon>copy</Icon>
                </IconButton>
              )}
              {permissions.delete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickTemplate.remove(template.id);
                  }}
                >
                  <Icon>delete</Icon>
                </IconButton>
              )}
            </MDBox>
          ),
        };
      }) || []
    );
  }, [templates]);

  return (
    <MDBox pt={6} pb={3}>
      <TemplateDialog
        open={Boolean(open.templateDialog)}
        mode={open.templateDialog?.mode}
        onClose={() => {
          changeOpen({ field: "templateDialog", value: false });
        }}
        onSubmit={async (data) => {
          if (open.templateDialog?.mode === "create")
            return await handleCreateAuditTemplate(data);

          if (open.templateDialog?.mode === "request")
            return await handleCreateAuditTemplate(data, true);

          if (open.templateDialog?.mode === "update")
            return await handleUpdateAuditTemplate(data.id, data);
        }}
        initial={open.templateDialog?.initial}
      />
      <AlertDialog
        open={open.alertRemoveDialog}
        setOpen={(value) => {
          changeOpen({ field: "alertRemoveDialog", value });
        }}
        title="Delete template?"
        content={
          "Are you sure you want to delete this template? This action is irreversible!"
        }
        onOk={async () => {
          await handleRemoveAuditTemplate(open.alertRemoveDialog.id);
        }}
      />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                {label("template-plural")}
              </MDTypography>
              <MDBox sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                {permissions.create && (
                  <MDButton
                    onClick={() => {
                      changeOpen({
                        field: "templateDialog",
                        value: { mode: "create" },
                      });
                    }}
                  >
                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                    &nbsp;{label("add-new-template-button")}
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
            {permissions.get ? (
              <>
                <MDBox p={2}>
                  <TableSearch
                    id="all-audit-templates"
                    internalLoading={!Boolean(originalTemplates)}
                    fullWidth
                    setData={setOriginalTemplates}
                    onSearch={async (request, callback) => {
                      const newQuery = {
                        ...query,
                        ...request,
                        skip: (Number(searchParams.get("page") || 1) - 1) * 10,
                        take: 10,
                      };

                      setQuery(newQuery);
                      callback(await handleFetchAuditTemplates(newQuery));
                    }}
                    label={label("search-templates-label")}
                  />
                </MDBox>
                <MDBox
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderBottomRightRadius: "16px",
                    borderBottomLeftRadius: "16px",
                  }}
                >
                  <DataTable
                    loading={!Boolean(templates)}
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    includeDetails={(row) => {
                      const template = row.original.data;

                      return (
                        <RowTemplate2
                          key={template?.id}
                          template={template}
                          onClick={onClickTemplate}
                        />
                      );
                    }}
                  />
                  {loading && (
                    <MDBox
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        zIndex: 5,
                      }}
                    >
                      <CircularProgress
                        disableShrink
                        color={"info"}
                        thickness={"5"}
                      />
                    </MDBox>
                  )}
                  <MDBox sx={{ padding: 2 }}>
                    <Pagination
                      count={Math.floor(count / 10) + 1}
                      color={"info"}
                      variant="outlined"
                      page={Number(searchParams.get("page"))}
                      onChange={(_, newPage) => {
                        setSearchParams((curr) => {
                          curr.set("page", newPage);
                          return curr;
                        });

                        const newQuery = {
                          ...query,
                          skip:
                            (Number(searchParams.get("page") || 1) - 1) * 10,
                          take: 10,
                        };

                        setQuery(newQuery);

                        handleFetchAuditTemplates({
                          ...query,
                          skip: (Number(newPage || 1) - 1) * 10,
                        });
                      }}
                    />
                  </MDBox>
                  {/* {templates?.length
                    ? templates?.map((template) => {
                        return (
                          <RowTemplate
                            key={template?.id}
                            template={template}
                            onClick={onClickTemplate}
                          />
                        );
                      })
                    : null}

                  {!templates?.length && templates && (
                    <MDTypography
                      sx={{
                        width: "100%",
                        height: "200px",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        fontSize: "1rem",
                        fontWeight: "500",
                      }}
                    >
                      {label("no-templates-were-found")}
                    </MDTypography>
                  )}
                  {!templates?.length && !templates && (
                    <MDTypography
                      sx={{
                        width: "100%",
                        height: "200px",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        fontSize: "1rem",
                        fontWeight: "500",
                      }}
                    >
                      {label("loading")}
                    </MDTypography>
                  )} */}
                </MDBox>
              </>
            ) : (
              <AccessDenied type="component" />
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
